import { type FC, Suspense, useRef, useState } from 'react';
import { type RelayProp, graphql, createFragmentContainer } from 'react-relay';
import classnames from 'classnames';
import { dibsLazyClient } from 'dibs-lazy/exports/dibsLazyClient';
import { useIntl } from 'dibs-react-intl';

import { messages } from '../messages/pdpDealerDetailsMessages';
import { trackSellerDetailsView } from '../tracking/pdpTracking';
import {
    MAKE_OFFER,
    CONTACT_SELLER,
} from '../PdpSharedContactTabsVariant/PdpSharedSellerTabsContants';

// components
import { VisibilityTracker } from 'dibs-visibility-tracker/exports/VisibilityTracker';
import { PdpSharedDealerDistinction } from '../PdpSharedDealerDistinctions/PdpSharedDealerDistinction';
import { PdpSharedCallDealerCta } from '../PdpSharedCallDealerCta/PdpSharedCallDealerCta';
import { ContactDealerInlineFormLoader } from 'dibs-buyer-layout/exports/ContactDealerInlineFormLoader';
import { PdpSharedSellerTabsVariantLazy } from '../PdpSharedContactTabsVariant/PdpSharedSellerTabsVariantLazy';
import { PdpSharedDealerDetailsBio } from '../PdpSharedDealerDetailsBio/PdpSharedDealerDetailsBio';
import { PdpSharedDealerDetailsHeader } from '../PdpSharedDealerDetailsHeader/PdpSharedDealerDetailsHeader';
import { PdpSharedDealerInfo } from '../PdpSharedDealerInfo/PdpSharedDealerInfo';

// types
import { type PdpRespDealerDetails_viewer$data } from './__generated__/PdpRespDealerDetails_viewer.graphql';
import { type PdpRespDealerDetails_item$data } from './__generated__/PdpRespDealerDetails_item.graphql';
import { type PdpRespDealerDetails_user$data } from './__generated__/PdpRespDealerDetails_user.graphql';
import { type PdpRespDealerDetails_sku$data } from './__generated__/PdpRespDealerDetails_sku.graphql';

// styles
import styles from './PdpRespDealerDetails.scss';

const PdpSharedSellerDetailsReviewContainer = dibsLazyClient(
    () =>
        import(
            /* webpackChunkName: "PdpSharedSellerDetailsReviewContainer" */ '../shared/PdpSharedSellerDetails/PdpSharedSellerDetailsReviewContainer'
        )
);

type Props = {
    viewer: PdpRespDealerDetails_viewer$data;
    item: PdpRespDealerDetails_item$data;
    user: PdpRespDealerDetails_user$data | null | undefined;
    sku: PdpRespDealerDetails_sku$data | null | undefined;
    relay: RelayProp;
    afterDisplay: boolean;
    currency: string;
    dataTn?: string;
    loadForms?: boolean;
    hideBorder?: boolean;
};

const PdpRespDealerDetailsComponent: FC<Props> = ({
    item,
    sku,
    user,
    viewer,
    currency,
    afterDisplay,
    dataTn,
    loadForms: loadFormsRef,
    hideBorder,
}) => {
    const intl = useIntl();
    const sellerDetailsRef = useRef(null);
    const [loadForms, setLoadForms] = useState(loadFormsRef || false);

    const { seller, dealerDetailsDisplayInfo } = item;
    const { isRewardsTier2, liveStorefrontProfile } = seller || {};
    const aboutUs = liveStorefrontProfile?.aboutUsUnbranded;
    const isNegotiable = dealerDetailsDisplayInfo?.ctas?.showMakeOffer || false;
    const shouldLoadEmbeddedMakeOffer = afterDisplay && isNegotiable;
    const shouldLoadContactForm = afterDisplay && !isNegotiable;

    if (!seller?.isAccessible) {
        return null;
    }

    const header =
        isRewardsTier2 && aboutUs ? (
            <>
                <div className={styles.detailsHeaderBio} data-tn="pdp-dealer-details-header-bio">
                    <div className={styles.leftSection}>
                        <PdpSharedDealerDetailsHeader
                            seller={seller}
                            user={user}
                            afterDisplay={afterDisplay}
                        />
                    </div>
                    <div className={`${styles.detailsBio} ${styles.rightSection}`}>
                        <PdpSharedDealerDetailsBio seller={seller} afterDisplay={afterDisplay} />
                    </div>
                </div>
                <div className={`${styles.detailsStatsContainer} ${styles.leftSection}`}>
                    <div className={styles.detailsSellerStats}>
                        <div className={styles.detailsSellerStatsColumn}>
                            <PdpSharedDealerDistinction seller={seller} />
                        </div>
                        <div className={styles.detailsSellerStatsColumn}>
                            <PdpSharedDealerInfo seller={seller} />
                        </div>
                    </div>
                    {afterDisplay && (
                        <Suspense fallback={null}>
                            <PdpSharedSellerDetailsReviewContainer
                                viewer={viewer}
                                seller={seller}
                            />
                        </Suspense>
                    )}
                </div>
            </>
        ) : (
            <div className={`${styles.detailsContainer} ${styles.leftSection}`}>
                <div className={styles.detailsHeader} data-tn="pdp-dealer-info">
                    <PdpSharedDealerDetailsHeader
                        seller={seller}
                        user={user}
                        afterDisplay={afterDisplay}
                    />
                </div>
                <div className={styles.detailsSellerStats}>
                    <div className={styles.detailsSellerStatsColumn}>
                        <PdpSharedDealerDistinction seller={seller} />
                    </div>
                    <div className={styles.detailsSellerStatsColumn}>
                        <PdpSharedDealerInfo seller={seller} />
                    </div>
                </div>
                {afterDisplay && (
                    <Suspense fallback={null}>
                        <PdpSharedSellerDetailsReviewContainer viewer={viewer} seller={seller} />
                    </Suspense>
                )}
            </div>
        );

    return (
        <>
            <div
                className={classnames(styles.wrapper, {
                    [styles.hideBorder]: hideBorder,
                })}
                ref={sellerDetailsRef}
                data-tn={dataTn}
            >
                <VisibilityTracker
                    elementRef={sellerDetailsRef}
                    onVisibilityChange={({ isVisible }) => {
                        setLoadForms(isVisible);
                        trackSellerDetailsView(isNegotiable, isVisible);
                    }}
                />
                {header}

                <div
                    className={classnames(styles.formContainer, styles.rightSection, {
                        [styles.hideBorder]: hideBorder,
                    })}
                    data-tn="pdp-contact-dealer-info"
                >
                    {shouldLoadEmbeddedMakeOffer && loadForms ? (
                        <Suspense fallback={''}>
                            <PdpSharedSellerTabsVariantLazy
                                viewer={viewer}
                                item={item}
                                sku={sku}
                                user={user}
                                buttonProps={{ size: 'medium' }}
                                currency={currency}
                                tabOrder={
                                    user?.isVerifiedTrade
                                        ? [CONTACT_SELLER, MAKE_OFFER]
                                        : [MAKE_OFFER, CONTACT_SELLER]
                                }
                            />
                        </Suspense>
                    ) : (
                        <ContactDealerInlineFormLoader
                            viewer={viewer}
                            load={shouldLoadContactForm && loadForms}
                            item={item}
                            user={user}
                            responseTimePos="none"
                            inputProps={{
                                hasAnimatedPlaceholder: true,
                                placeholder: intl.formatMessage(messages.namePlaceholder),
                            }}
                            buttonProps={{ className: styles.contactDealerButton }}
                        />
                    )}
                    {shouldLoadContactForm && loadForms && (
                        <PdpSharedCallDealerCta item={item} user={user} />
                    )}
                </div>
            </div>
        </>
    );
};

export const PdpRespDealerDetails = createFragmentContainer(PdpRespDealerDetailsComponent, {
    viewer: graphql`
        fragment PdpRespDealerDetails_viewer on Viewer {
            ...ContactDealerInlineForm_viewer @include(if: $afterDisplay)
            ...PdpSharedSellerTabsVariant_viewer
            ...PdpSharedSellerDetailsReviewContainer_viewer @include(if: $afterDisplay)
        }
    `,
    user: graphql`
        fragment PdpRespDealerDetails_user on User {
            isVerifiedTrade
            ...ContactDealerInlineForm_user
            ...PdpSharedSellerTabsVariant_user
            ...PdpSharedCallDealerCta_user
            ...PdpSharedDealerDetailsHeader_user
        }
    `,
    item: graphql`
        fragment PdpRespDealerDetails_item on Item {
            ... @include(if: $fetchPdp) {
                ...PdpSharedCallDealerCta_item
                ...ContactDealerInlineForm_item
                ...PdpSharedSellerTabsVariant_item
                dealerDetailsDisplayInfo: displayInfo {
                    ctas {
                        showMakeOffer
                    }
                }
                seller {
                    __typename
                }
            }
            seller {
                ...PdpSharedSellerDetailsReviewContainer_seller @include(if: $afterDisplay)
                ...PdpSharedDealerDetailsBio_seller
                ...PdpSharedDealerDetailsHeader_seller
                ...PdpSharedDealerDistinction_seller
                ...PdpSharedDealerInfo_seller
                isRewardsTier2: isAnchorListMember(listName: "SELLER_REWARDS_2")
                liveStorefrontProfile {
                    aboutUsUnbranded
                }
                isAccessible
            }
        }
    `,
    sku: graphql`
        fragment PdpRespDealerDetails_sku on ItemSkuType {
            ...PdpSharedSellerTabsVariant_sku
        }
    `,
});
